import { useEffect, useRef, useState } from 'react';
import ParticleBackground from './ParticleBackground';

const stats = [
  { value: '200+', label: 'Proyectos completados' },
  { value: '80+', label: 'Instituciones y empresas' },
  { value: '10M+', label: 'Datos procesados.' },
  { value: '10+', label: 'Años de experiencia' },
];

export default function HeroSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleCTAClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const el = document.querySelector('#services');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="home"
      ref={sectionRef}
      className="relative min-h-screen flex items-center overflow-hidden bg-biodata-dark"
    >
      {/* Particle Background */}
      <ParticleBackground />

      {/* Radial Glow */}
      <div className="absolute inset-0 bg-hero-glow pointer-events-none" style={{ zIndex: 2 }} />

      {/* Floating Orbs */}
      <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-biodata-teal/5 blur-3xl animate-pulse-slow pointer-events-none" style={{ zIndex: 2 }} />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-biodata-emerald/5 blur-3xl animate-pulse-slow pointer-events-none" style={{ zIndex: 2 }} />

      {/* Content */}
      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 pt-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center min-h-[calc(100vh-5rem)]">
            {/* Left Content */}
            <div
              className={`transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-biodata-teal/10 border border-biodata-teal/20 mb-6">
                <span className="w-2 h-2 rounded-full bg-biodata-emerald animate-pulse" />
                <span className="text-sm text-biodata-teal font-medium">Investigación Científica Aplicada</span>
              </div>

              {/* Slogan */}
              <p className="text-sm md:text-base text-biodata-teal font-medium tracking-widest uppercase mb-4">
                Entender. Integrar. Transformar.
              </p>

              {/* Headline */}
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                Transformamos{' '}
                <span className="text-gradient italic">datos biológicos</span>
                <br />
                en{' '}
                <span className="text-gradient italic">conocimiento</span>
                <br />
                verificable y útil
              </h1>

              {/* Subtitle */}
              <p className="text-base md:text-lg text-biodata-muted leading-relaxed mb-8 max-w-xl">
                ANALITICA es una empresa mexicana especializada en bioinformática, 
                ciencia de datos, biología de sistemas e infraestructura analítica. 
                Resolvemos problemas complejos en sistemas vivos con rigor científico.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <a
                  href="#services"
                  onClick={handleCTAClick}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-biodata-teal text-white font-medium hover:bg-biodata-teal/90 transition-all duration-300 shadow-lg shadow-biodata-teal/25 hover:shadow-biodata-teal/40 whitespace-nowrap"
                >
                  <span>Conoce nuestros servicios</span>
                  <i className="ri-arrow-right-line" />
                </a>
                <a
                  href="#about"
                  onClick={(e) => {
                    e.preventDefault();
                    const el = document.querySelector('#about');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-biodata-muted/30 text-biodata-muted hover:text-white hover:border-biodata-teal/50 transition-all duration-300 whitespace-nowrap"
                >
                  <span>Sobre nosotros</span>
                  <i className="ri-play-circle-line" />
                </a>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                {stats.map((stat, index) => (
                  <div
                    key={stat.label}
                    className={`transition-all duration-700 delay-${index * 150} ${
                      isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                    }`}
                    style={{ transitionDelay: `${index * 150}ms` }}
                  >
                    <div className="glass-card rounded-xl p-4 text-center">
                      <div className="text-xl md:text-2xl font-display font-bold text-biodata-teal mb-1">
                        {stat.value}
                      </div>
                      <div className="text-xs text-biodata-muted leading-tight">
                        {stat.label}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Visual */}
            <div
              className={`hidden lg:flex items-center justify-center transition-all duration-1000 delay-300 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
              }`}
            >
              <div className="relative w-full max-w-lg">
                {/* Main Image */}
                <div className="relative rounded-2xl overflow-hidden aspect-[4/3] glow-teal">
                  <img
                    src="https://readdy.ai/api/search-image?query=Abstract%20biotechnology%20data%20visualization%20with%20DNA%20double%20helix%20merged%20with%20digital%20data%20streams%20and%20neural%20network%20nodes%2C%20dark%20background%20with%20teal%20and%20emerald%20glowing%20particles%2C%20scientific%20computing%20aesthetic%2C%20futuristic%20bioinformatics%20art%2C%20high%20detail%20digital%20render&width=800&height=600&seq=hero1&orientation=landscape"
                    alt="ANALITICA - Visualización de datos bioinformáticos"
                    className="w-full h-full object-cover"
                    loading="eager"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-biodata-dark/60 via-transparent to-biodata-dark/20" />
                </div>

                {/* Floating Cards */}
                <div className="absolute -top-4 -left-4 glass-card rounded-xl p-3 animate-float">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-biodata-emerald/20 flex items-center justify-center">
                      <i className="ri-dna-line text-biodata-emerald text-sm" />
                    </div>
                    <div>
                      <div className="text-xs text-white font-medium">Secuenciación</div>
                      <div className="text-[10px] text-biodata-muted">NGS Analysis</div>
                    </div>
                  </div>
                </div>

                <div className="absolute -bottom-4 -right-4 glass-card rounded-xl p-3 animate-float" style={{ animationDelay: '2s' }}>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-biodata-cyan/20 flex items-center justify-center">
                      <i className="ri-brain-line text-biodata-cyan text-sm" />
                    </div>
                    <div>
                      <div className="text-xs text-white font-medium">Machine Learning</div>
                      <div className="text-[10px] text-biodata-muted">Modelos Predictivos</div>
                    </div>
                  </div>
                </div>

                <div className="absolute top-1/2 -right-8 glass-card rounded-xl p-3 animate-float" style={{ animationDelay: '1s' }}>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-biodata-teal/20 flex items-center justify-center">
                      <i className="ri-bar-chart-box-line text-biodata-teal text-sm" />
                    </div>
                    <div>
                      <div className="text-xs text-white font-medium">Análisis</div>
                      <div className="text-[10px] text-biodata-muted">Big Data</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}